import { createSlice, freeze, PayloadAction } from '@reduxjs/toolkit';
import { isNil } from 'lodash';
// Import Types
import type { ActivePartner, Currency, HeaderPartner, HeaderPartnerCurrency, Language } from 'types';
// Import Constants
import { TimeFormat } from 'constants/common';

interface HeaderNavigation {
    route: string;
    displayNameKey: string;
    url: string;
}

export interface HeaderSliceState {
    isLoading: boolean;
    navigation: HeaderNavigation;
    timezone: string | null;
    languages: Language[];
    partnerCurrencies: HeaderPartnerCurrency[];
    partners: HeaderPartner[];
    colorTheme: 'light' | 'dark';
    timeFormat: string;
}

const initialState: HeaderSliceState = {
    isLoading: false,
    navigation: {
        route: '',
        displayNameKey: '',
        url: '',
    },
    timezone: null,
    languages: [],
    partnerCurrencies: [],
    partners: [],
    colorTheme: 'light',
    timeFormat: TimeFormat,
};

export const headerSlice = createSlice({
    name: 'header',
    initialState,
    reducers: (create) => ({
        getHeaderPartnersFetch: create.reducer((state) => {
            state.isLoading = true;
        }),
        getHeaderPartnersSucceeded: create.reducer(
            (state, { payload }: PayloadAction<{ partners: ActivePartner[]; currencies: Currency[] }>) => {
                state.isLoading = false;
                if (isNil(payload)) return;

                // The list is large (thousands of partners). A frozen value is not walked by Immer,
                // which otherwise traverses and deep-freezes every partner object and blocks the main thread.
                state.partners = freeze(
                    payload.partners.map((data) => ({
                        ...data,
                        value: data.PartnerId,
                        label: data.Name,
                    })),
                );
                state.partnerCurrencies = payload.currencies.map(({ CurrencyId, Name, Precision }) => ({
                    value: CurrencyId,
                    label: `${Name} (${CurrencyId})`,
                    Precision,
                }));
            },
        ),
        getHeaderPartnersFailed: create.reducer((state) => {
            state.isLoading = false;
        }),

        setHeaderTimeZone: create.reducer((state, { payload }: PayloadAction<string>) => {
            state.timezone = payload;
        }),

        setHeaderAppUrl: create.reducer((state, { payload }: PayloadAction<string>) => {
            state.navigation.url = payload;
        }),
        setHeaderNavigation: create.reducer((state, { payload }: PayloadAction<Nilable<AnyObject>>) => {
            if (isNil(payload)) return;
            state.navigation.route = payload.route;
            state.navigation.displayNameKey = payload.name;
        }),

        setHeaderLanguagesList: create.reducer((state, { payload }: PayloadAction<Language[]>) => {
            state.languages = payload;
        }),
    }),
    selectors: {
        selectHeader: (header) => header,
        selectHeaderNavigation: (header) => header.navigation,
        selectHeaderAppUrl: (header) => header.navigation.url,
        selectHeaderPartners: (header) => header.partners,
        selectHeaderPartnerCurrencies: (header) => header.partnerCurrencies,
        selectHeaderLanguages: (header) => header.languages,
        selectHeaderTimeFormat: (header) => header.timeFormat,
    },
});

export const {
    getHeaderPartnersFetch,
    getHeaderPartnersSucceeded,
    getHeaderPartnersFailed,

    setHeaderAppUrl,
    setHeaderNavigation,

    setHeaderLanguagesList,
} = headerSlice.actions;

export const {
    selectHeader,
    selectHeaderNavigation,
    selectHeaderAppUrl,
    selectHeaderPartners,
    selectHeaderPartnerCurrencies,
    selectHeaderLanguages,
    selectHeaderTimeFormat,
} = headerSlice.selectors;
